// Shared layout model for the digitised VQF Form A / Form K, per Jaybee's review guidance
// (Xavier Palma, 16 Jul 2026): full VQF form titles, a Contracting Partner block first
// (sourced from the verified company record, not typed by the signer), then the declaration
// fields in the prescribed order, closing with the confirmation evidence (IP + date + OTP).
// Consumed by the declaration modals (MLRO dashboard + Jaybee portal) and the Form
// Confirmation PDF included in the case-file ZIP export (Art. 63 VQF Regulations).

export const VQF_FORM_TITLES = {
  K: "Form K — Establishing of the controlling person of operating legal entities and partnerships both not quoted on the stock exchange",
  A: "Form A — Establishing of the beneficial owner of the assets",
};

// Canonical presentation order. Sumsub questionnaire item ids → display labels.
const FIELD_ORDER = [
  ["question1", "Declaration basis"],
  ["lastNames", "Last name(s)"],
  ["firstNames", "First name(s)"],
  ["dateOfBirth", "Date of birth"],
  ["nationality", "Nationality"],
  ["actualAddressOfDomic", "Domicile"],
  ["fiduciaryQuestionLab", "Fiduciary holding of assets (third-party beneficial owner)"],
  ["informationUpdateObl", "Information Update Obligation"],
  ["pleaseConfirmThatYou", "False declaration warning (Art. 251 SCC)"],
];

// Ids that are structural (section wrappers / entity-type context) rather than declaration fields.
const NON_FIELD_IDS = new Set(["formKEstablishingOfT", "formAOfTheBeneficial", "pleaseChooseLegalEnt"]);

const itemValue = (item, valueLabel) => {
  if (!item) return null;
  if (item.value !== undefined && item.value !== null && item.value !== "") return valueLabel(item.value);
  if (Array.isArray(item.values) && item.values.length) return item.values.map(valueLabel).join(", ");
  return null;
};

// record: sumsub_form_requests row · questionnaire: {sections:{id:{items:{id:{value|values}}}}}
// session / report: onboarding session + report JSONB · fieldLabel/valueLabel: caller's label maps
export function buildVqfFormModel({ record, questionnaire, session, report, fieldLabel, valueLabel }) {
  const cd = (report && report.companyDetails) || {};
  const items = {};
  Object.values((questionnaire && questionnaire.sections) || {}).forEach((section) => {
    Object.entries((section && section.items) || {}).forEach(([id, item]) => { items[id] = item; });
  });

  const fields = [];
  const used = new Set(NON_FIELD_IDS);
  FIELD_ORDER.forEach(([id, label]) => {
    used.add(id);
    const v = itemValue(items[id], valueLabel);
    if (v !== null) fields.push({ id, label, value: v });
  });
  // Any additional questionnaire fields not in the canonical order render after, unlabelled loss-free.
  Object.entries(items).forEach(([id, item]) => {
    if (used.has(id)) return;
    const v = itemValue(item, valueLabel);
    if (v !== null) fields.push({ id, label: fieldLabel(id), value: v });
  });

  const fidItem = items["fiduciaryQuestionLab"];
  const fidRaw = fidItem && (fidItem.value || (Array.isArray(fidItem.values) ? fidItem.values[0] : null));
  const fiduciaryYes = String(fidRaw || "").toLowerCase() === "yes";

  return {
    formType: record && record.form_type,
    title: VQF_FORM_TITLES[record && record.form_type] || `Form ${record && record.form_type}`,
    signerName: (record && record.signer_name) || "",
    contractingPartner: {
      legalName: cd.legalName || (session && session.company_name) || "",
      address: cd.registeredAddress || "",
      country: cd.jurisdiction || "",
      registrationNumber: cd.registrationNumber || "",
    },
    fields,
    fiduciaryYes,
    confirmation: {
      at: record && record.confirmed_at,
      ip: record && record.confirmation_ip,
      actionId: record && record.applicant_action_id,
    },
  };
}
